// components/MasteryMappa.tsx
//
// V28-C (blueprint C8). The 25-argomento mastery map for the Progressi cockpit
// (S2): a 5-band legend with live counts on top, then a 2-column grid of
// `MasteryCell` tiles. Maps to the prototype `.legend` + `.mappa`
// (kit.css 743-815 / progressi.html 80-123). Replaces the auto-fit MasteryRing
// grid for THIS cockpit; MasteryRing.tsx stays in use elsewhere.
//
// Single source of truth: each item is binned via `masteryBand()` and coloured
// via `masteryBandColors()` from tokens — the same path MasteryCell + MasteryRing
// use. The legend counts are derived from those same bins, so legend + cells can
// never disagree. Light-only (tokens.mode hard-pinned to 'light', theme.tsx F1).
//
// Purely presentational — the caller supplies the argomenti + an optional select
// handler; no fetch, no state.

import type { JSX } from 'react';
import { useTokens } from '../state/theme';
import { MasteryCell } from './MasteryCell';
import {
  masteryBand,
  masteryBandColors,
  MASTERY_BANDS,
  MASTERY_BAND_ORDER,
  type MasteryBandId,
  type ThemeMode,
} from '../tokens';

export interface MasteryMappaItem {
  argomentoId: string;
  /** Display order, rendered zero-padded by MasteryCell. */
  order: number;
  nameIt: string;
  averageMastery: number;
  principlesTotal: number;
  principlesObserved?: number;
}

export interface MasteryMappaProps {
  items: MasteryMappaItem[];
  /** Optional click handler, forwarded to each cell. */
  onSelect?: (argomentoId: string) => void;
}

/** Singular/plural Italian label for a band count, e.g. 1 → "1 Forte", 5 → "5 Forti". */
const BAND_COUNT_LABEL: Record<MasteryBandId, (n: number) => string> = {
  forte:        (n) => `${n} ${n === 1 ? 'Forte' : 'Forti'}`,
  buono:        (n) => `${n} ${n === 1 ? 'Buono' : 'Buoni'}`,
  daRinforzare: (n) => `${n} Da rinforzare`,
  debole:       (n) => `${n} ${n === 1 ? 'Debole' : 'Deboli'}`,
  daIniziare:   (n) => `${n} Da esplorare`,
};

export function MasteryMappa({ items, onSelect }: MasteryMappaProps): JSX.Element {
  const tokens = useTokens();
  const mode: ThemeMode = tokens.mode;

  // Tally each item into its band — the legend reads from the SAME binning the
  // cells use, so they can never drift apart.
  const counts: Record<MasteryBandId, number> = {
    forte: 0, buono: 0, daRinforzare: 0, debole: 0, daIniziare: 0,
  };
  for (const it of items) {
    const band = masteryBand({
      averageMastery: it.averageMastery,
      principlesTotal: it.principlesTotal,
      principlesObserved: it.principlesObserved,
    });
    // band.id is the kebab id ('da-rinforzare'); map back to the keyed id.
    const key = (Object.keys(MASTERY_BANDS) as MasteryBandId[])
      .find((k) => MASTERY_BANDS[k].id === band.id);
    if (key) counts[key] += 1;
  }

  return (
    <div data-testid="mastery-mappa">
      {/* 5-band legend with live counts (prototype `.legend`). */}
      <div
        data-testid="mastery-mappa-legend"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px 12px',
          marginBottom: 14,
        }}
      >
        {MASTERY_BAND_ORDER.map((bandId) => {
          const band = MASTERY_BANDS[bandId];
          const isEmpty = bandId === 'daIniziare';
          const { color } = masteryBandColors(band, mode);
          return (
            <span
              key={bandId}
              data-testid={`mastery-legend-${band.id}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontFamily: tokens.fonts.body,
                fontSize: 11.5,
                color: tokens.textMuted,
              }}
            >
              <i
                aria-hidden="true"
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 3,
                  display: 'inline-block',
                  background: isEmpty ? tokens.surface : color,
                  border: isEmpty
                    ? `1px dashed ${tokens.lineStrong}`
                    : `1px solid ${color}`,
                }}
              />
              {BAND_COUNT_LABEL[bandId](counts[bandId])}
            </span>
          );
        })}
      </div>

      {/* DP4 (founder device pass 2026-07-13): after one simulation the map
          showed 11 "Deboli" with zero lessons done — truthful but confusing.
          Say WHERE the colors come from so the simulation→map link is
          explicit instead of implied. */}
      <p
        data-testid="mastery-mappa-caption"
        style={{
          margin: '0 0 12px',
          fontFamily: tokens.fonts.body,
          fontSize: 12,
          lineHeight: 1.45,
          color: tokens.textMuted,
        }}
      >
        I colori nascono dalle tue risposte reali: i quiz delle lezioni e le
        domande delle simulazioni d'esame. Anche una sola simulazione colora la
        mappa — completa le lezioni per costruire la base.
      </p>

      {/* 2-column grid of named + numbered cells (prototype `.mappa`). */}
      <div
        data-testid="mastery-mappa-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 8,
        }}
      >
        {items.map((it) => (
          <MasteryCell
            key={it.argomentoId}
            argomentoId={it.argomentoId}
            order={it.order}
            nameIt={it.nameIt}
            averageMastery={it.averageMastery}
            principlesTotal={it.principlesTotal}
            principlesObserved={it.principlesObserved}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}
